import { ActionMode, GeneralStatusEnum, KindergartenDetailsTabs } from '../../../common/enums/common.enum';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import GenericTextField from '../../../components/GenericTextField/GenericTextField';
import GenericStatusSwitch from '../../../components/GenericStatusSwitch';
import { createNotification, getStatusColor } from '../../../utilities/helper/notification.helper';
import { CustomErrorStatus, MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { createTeacher, getLocationsWIthGroups, getTeacherDetails, updateTeacher } from '../../../api/kindergarten.api';
import { CreateTeacherDto, LocationDto, TeacherDetailsDto, UpdateTeacherDto } from '../../../common/interfaces/kindergarten.interface';
import GenericLocationGroupSelector from '../../../components/GenericLocationGroupSelector/GenericLocationGroupSelector';
import { ReactComponent as EditIcon } from '../../../assets/edit-icon.svg';
import { ReactComponent as CircleIcon } from '../../../assets/circle-icon.svg';
import { ProfilePageBox, ProfilePictureAvatar } from '../../../common/styles/Common.styles';
import { useTranslation } from 'react-i18next';

interface TeacherPageProps {
  mode: ActionMode;
}

const TeacherPage: React.FC<TeacherPageProps> = ({ mode }) => {
  const { t } = useTranslation();
  const { teacherId, kindergartenId, kindergartenName } = useParams<{
    teacherId?: string;
    kindergartenId: string;
    kindergartenName: string;
  }>();

  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<GeneralStatusEnum>(GeneralStatusEnum.ACTIVE);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [locations, setLocations] = useState<LocationDto[]>([]);
  const [teacherGroups, setTeacherGroups] = useState<string[]>([]);
  const [initialTeacher, setInitialTeacher] = useState<TeacherDetailsDto>();

  const [isEditing, setIsEditing] = useState(mode === ActionMode.CREATE || mode === ActionMode.EDIT);
  const [currentMode, setCurrentMode] = useState<ActionMode>(mode);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);

  useEffect(() => {
    if (currentMode === ActionMode.CREATE) {
      fetchLocationsWithGroup();
    }
    if (currentMode === ActionMode.EDIT || (currentMode === ActionMode.DETAILS && teacherId)) {
      fetchTeacherDetails();
    }
  }, [kindergartenId, mode, teacherId]);

  const handleSave = async () => {
    const success = await saveTeacher();
    if (success) {
      navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.Teachers}`);
    }
  };

  const handleSaveAndCreateAnother = async () => {
    const success = await saveTeacher();
    if (success) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setStatus(GeneralStatusEnum.ACTIVE);
      setTeacherGroups([]);
    }
  };
  const saveTeacher = async () => {
    if (!kindergartenId) return;

    if (!firstName.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, t('people.validation.firstNameRequired'));
      return false;
    }
    if (!lastName.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, t('people.validation.lastNameRequired'));
      return false;
    }
    if (currentMode === ActionMode.CREATE) {
      if (!email.trim()) {
        createNotification(MESSAGE_TYPE.ERROR, t('people.validation.emailRequired'));
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        createNotification(MESSAGE_TYPE.ERROR, t('people.validation.emailInvalid'));
        return false;
      }
    }

    try {
      if (currentMode === ActionMode.CREATE) {
        const teacherPayload: CreateTeacherDto = {
          email: email,
          firstName: firstName,
          lastName: lastName,
          phoneNumber: phone.trim() ? phone.trim() : undefined,
          status: status
        };
        if (teacherGroups.length > 0) {
          teacherPayload.groupIds = teacherGroups;
        }
        await createTeacher(kindergartenId, teacherPayload);
        createNotification(MESSAGE_TYPE.SUCCESS, t('teachers.created'));
      } else if (currentMode === ActionMode.EDIT && teacherId) {
        const teacherPayload: UpdateTeacherDto = {};
        if (firstName !== initialTeacher?.firstName) teacherPayload.firstName = firstName;
        if (lastName !== initialTeacher?.lastName) teacherPayload.lastName = lastName;
        if (phone !== initialTeacher?.phone) teacherPayload.phoneNumber = phone;
        if (status !== initialTeacher?.status) teacherPayload.status = status;
        const initialGroupIds =
          initialTeacher?.locations
            ?.flatMap(loc => loc.groups?.filter(g => g.assigned).map(g => g.id) ?? [])
            .filter((id): id is string => id !== undefined) ?? [];

        const currentGroupIds = teacherGroups;

        const groupsChanged =
          initialGroupIds.length !== currentGroupIds.length || !initialGroupIds.every(id => currentGroupIds.includes(id));

        if (groupsChanged) {
          teacherPayload.groupIds = currentGroupIds;
        }
        if (Object.keys(teacherPayload).length > 0) {
          await updateTeacher(kindergartenId, teacherId, teacherPayload);
          createNotification(MESSAGE_TYPE.SUCCESS, t('teachers.updated'));
        }
      }
      return true;
    } catch (error: any) {
      const backendStatus = error?.response?.data?.status;
      if (backendStatus === CustomErrorStatus.USER_EMAIL_ALREADY_EXISTS) {
        createNotification(MESSAGE_TYPE.ERROR, t('people.emailAlreadyRegistered'));
      } else if (backendStatus === CustomErrorStatus.TEACHER_NOT_ACTIVE) {
        createNotification(MESSAGE_TYPE.ERROR, t('teachers.inactiveCannotBeAssigned'));
      } else {
        createNotification(MESSAGE_TYPE.ERROR, error?.response?.message ? error?.response.message : t('teachers.saveFailed'));
      }
      return false;
    }
  };
  const fetchLocationsWithGroup = async () => {
    try {
      if (!kindergartenId) return;
      setIsLoadingLocations(true);
      const result = await getLocationsWIthGroups(kindergartenId, GeneralStatusEnum.ACTIVE);
      if (result.results) {
        setLocations(result.results);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('locations.loadFailed'));
    } finally {
      setIsLoadingLocations(false);
    }
  };
  const fetchTeacherDetails = async () => {
    if (!kindergartenId || !teacherId) return;
    try {
      setIsLoadingLocations(true);
      const response = await getTeacherDetails(kindergartenId, teacherId);
      if (response.results) {
        const teacher = response.results;
        setInitialTeacher(teacher);
        setFirstName(teacher.firstName);
        setLastName(teacher.lastName);
        setPhone(teacher.phone ?? '');
        setStatus(teacher.status);
        setEmail(teacher.email ?? '');
        setLocations(teacher.locations ?? []);
        const assignedGroupIds =
          teacher.locations
            ?.flatMap(loc => loc?.groups?.filter(g => g.assigned)?.map(g => g.id) ?? [])
            .filter((id): id is string => id !== undefined) ?? [];
        setTeacherGroups(assignedGroupIds);

        setAvatarUrl(teacher.profileImage ?? null);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('teachers.detailsLoadFailed'));
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const getPageTitle = () => {
    switch (currentMode) {
      case ActionMode.CREATE:
        return t('teachers.create');
      case ActionMode.EDIT:
        return t('teachers.edit');
      case ActionMode.DETAILS:
        return `${firstName} ${lastName}`;
    }
  };

  const isFormValid = useMemo(() => {
    return firstName.trim() && lastName.trim() && (mode === ActionMode.CREATE ? email.trim() : true);
  }, [firstName, lastName, email, mode]);

  return (
    <Box p={7}>
      <Breadcrumbs aria-label={t('common.breadcrumb')} sx={{ mb: 3 }}>
        <Link color="text.secondary" underline="hover" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          {t('nav.kindergartens')}
        </Link>
        {kindergartenName && (
          <Link
            color="text.secondary"
            underline="hover"
            onClick={() => navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.Teachers}`)}
            sx={{ cursor: 'pointer' }}
          >
            {kindergartenName}
          </Link>
        )}
        <Typography color="text.primary">{getPageTitle()}</Typography>
      </Breadcrumbs>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="44px">
        <Typography variant="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {currentMode === ActionMode.DETAILS && (
            <CircleIcon
              style={{
                color: getStatusColor(status),
                fontSize: 12
              }}
            />
          )}
          {currentMode === ActionMode.CREATE && t('teachers.create')}
          {currentMode === ActionMode.EDIT && t('teachers.edit')}
          {currentMode === ActionMode.DETAILS && `${firstName} ${lastName}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {mode === ActionMode.CREATE && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Secondary}
              variant="outlined"
              onClick={handleSaveAndCreateAnother}
              label={t('common.saveAndCreateAnother')}
              disabled={!isFormValid}
            />
          )}
          {isEditing && currentMode !== ActionMode.DETAILS && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              onClick={handleSave}
              label={t('common.save')}
              disabled={!isFormValid}
            />
          )}
          {currentMode === ActionMode.DETAILS && !isEditing && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              onClick={() => {
                setIsEditing(true);
                setCurrentMode(ActionMode.EDIT);
              }}
              label={t('common.edit')}
              startIcon={<EditIcon />}
            />
          )}
        </Box>
      </Box>

      <ProfilePageBox>
        {(mode === ActionMode.EDIT || mode === ActionMode.DETAILS) && (
          <ProfilePictureAvatar src={avatarUrl ?? undefined} alt={`${firstName} ${lastName}`} />
        )}
        <Box
          sx={{
            display: 'flex',
            width: '500px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '20px'
          }}
        >
          <GenericTextField
            label={t('people.firstName')}
            required={true}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            disabled={!isEditing}
          />
          <GenericTextField
            label={t('people.lastName')}
            required={true}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            disabled={!isEditing}
          />
          <GenericTextField
            label={t('common.email')}
            required={true}
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={mode !== ActionMode.CREATE}
          />

          <GenericTextField label={t('common.phone')} value={phone} onChange={e => setPhone(e.target.value)} disabled={!isEditing} />
          <GenericStatusSwitch
            label={t('teachers.status')}
            status={status}
            onChange={newStatus => setStatus(newStatus)}
            disabled={!isEditing}
          />
          <Typography variant="caption">{t('teachers.assignToGroup')}</Typography>

          {kindergartenId && (
            <GenericLocationGroupSelector
              kindergartenId={kindergartenId}
              locations={locations}
              selectedGroups={teacherGroups}
              onChange={group => setTeacherGroups(group)}
              disabled={!isEditing || status === GeneralStatusEnum.INACTIVE}
              isLoading={isLoadingLocations}
            />
          )}
        </Box>
      </ProfilePageBox>
    </Box>
  );
};
export default TeacherPage;
